import React, { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'antd';
import { isTextTruncated } from '../../../utils/truncationUtils';

/**
 * TruncatedCell Component
 * A table cell that intelligently shows tooltips based on text truncation
 * 
 * @param {string} children - The content to display in the cell
 * @param {string} defaultTooltip - Default tooltip text for non-truncated content
 * @param {string} className - Additional CSS classes
 * @param {Object} style - Inline styles
 * @param {boolean} showTooltip - Whether to show tooltip at all
 * @param {Object} otherProps - Other props to pass to the td element
 */
const TruncatedCell = ({ 
  children, 
  defaultTooltip = "Click to view full details", 
  className = "", 
  style = {}, 
  showTooltip = true,
  tooltipPlacement = "top",
  ...otherProps 
}) => {
  const cellRef = useRef(null);
  const [tooltipText, setTooltipText] = useState('');
  const [showTooltipComponent, setShowTooltipComponent] = useState(false);

  useEffect(() => {
    if (!showTooltip || !cellRef.current) return;

    const updateTooltip = () => {
      if (cellRef.current) {
        const content = typeof children === 'string' ? children : 
                       cellRef.current.textContent || cellRef.current.innerText || '';
        
        if (!content.trim()) {
          setShowTooltipComponent(false);
          return;
        }

        const isTruncated = isTextTruncated(cellRef.current);
        
        // Also check if any child elements (like badges) are truncated
        let childTruncated = false;
        if (!isTruncated) {
          const childElements = cellRef.current.querySelectorAll('span, div, .cBadge');
          for (let child of childElements) {
            if (isTextTruncated(child)) {
              childTruncated = true;
              break;
            }
          }
        }
        
        if (isTruncated || childTruncated) {
          setTooltipText(content);
          setShowTooltipComponent(true);
        } else if (defaultTooltip) {
          setTooltipText(defaultTooltip);
          setShowTooltipComponent(true);
        } else {
          setShowTooltipComponent(false);
        }
      }
    };

    // Initial setup
    updateTooltip();

    // Update on resize
    const handleResize = () => {
      setTimeout(updateTooltip, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [children, defaultTooltip, showTooltip]);

  const cellContent = (
    <td 
      ref={cellRef}
      className={className}
      style={style}
      {...otherProps}
    >
      {children}
    </td>
  );

  // Return tooltip wrapped content if tooltip should be shown
  if (showTooltip && showTooltipComponent && tooltipText) {
    return (
      <Tooltip 
        title={tooltipText}
        placement={tooltipPlacement}
        mouseEnterDelay={0.5}
        mouseLeaveDelay={0.1}
      >
        {cellContent}
      </Tooltip>
    );
  }

  // Return plain cell content if no tooltip needed
  return cellContent;
};

export default TruncatedCell;
